import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";

import Typography from "@/design-system/Typography";
import Button from "@/design-system/button";
import Carousel from "@/design-system/carousel";
import { FetchGet } from "@/lib/fetch-helper";
import type { ILogoTile } from "@/src/common/LogoTile";
import RoundTile from "@/src/common/RoundTile";
import { languageSelector, useLikedContext, useViewportInfoContext } from "@/src/contexts";
import { FavoritesResources } from "@/src/favorites-tab/Favorites.Resources";
import { FavoritesResourcesEn } from "@/src/favorites-tab/Favorites.Resources.En";
import FavoriteItem from "@/src/favorites-tab/favorite-item";
import { useQueryClientCustom, useQueryCustom } from "@/src/query-client-overrides";
import PageLayout from "@/src/shared/page-layout";
import type { IShoppingMallTile } from "@/src/shopping-mall/ShoppingMallTile";
import Container from "@Components/Container";
import { urlFormat } from "@Components/Modules/Helpers";
import DiscountTileV3 from "@Components/discount-tile";
import type { IDiscountTileV3 } from "@Components/discount-tile/discount-tile-v3";
import { FollowedBrands } from "@Routes/BrandLinks";
import { Favorites } from "@Routes/FavoritesLinks";
import { OffersIndex } from "@Routes/OffersLinks";
import { ShoppingMalls } from "@Routes/ShoppingMallLinks";
import * as stylex from "@stylexjs/stylex";

import ShoppingMallTile from "../../Components/src/shopping-mall-tile";
import { ComposableSpacing, colors } from "../../styles/tokens.stylex";

interface IFavoritesTabResult {
    brandsList: ILogoTile[];
    offersList: IDiscountTileV3[];
    shoppingMallsList: IShoppingMallTile[];
}

export function FavoritesTab() {
    const { isMobileOrTablet } = useViewportInfoContext();
    const queryClient = useQueryClientCustom();
    const [brands] = useLikedContext("brands");
    const [discounts] = useLikedContext("discounts");
    const [malls] = useLikedContext("malls");

    const [searchParams, updateSearchParams] = useSearchParams();

    useEffect(() => {
        return () => {
            queryClient.removeQueries({
                queryKey: ["favoritesTab"],
            });
        };
    }, [discounts, brands, malls, queryClient]);

    const { data, isLoading } = useQueryCustom({
        queryKey: ["favoritesTab"],
        queryFn: () => FetchGet<IFavoritesTabResult>(urlFormat(Favorites, { action: "RetrieveFavorites" })),
    });

    const resources = languageSelector(FavoritesResources, FavoritesResourcesEn);

    const menuList = [
        {
            text: resources.tab.all,
            tab: null,
        },
        {
            text: resources.tab.brands,
            tab: "brands",
        },
        {
            text: resources.tab.offers,
            tab: "offers",
        },
        {
            text: resources.tab.shoppingMalls,
            tab: "shoppingMalls",
        },
    ];

    const allowedTabs = menuList.map((item) => item.tab);
    let currentTab = searchParams.get("tab");

    if (!allowedTabs.includes(currentTab)) {
        currentTab = null;
    }

    const updateCurrentTab = (tab?: string) => {
        if (!tab) {
            updateSearchParams({}, { replace: true });
        } else {
            updateSearchParams({ tab }, { replace: true });
        }
    };

    const showAllTab = currentTab === null;

    return (
        <PageLayout breadcrumbs={[{ labelKey: "favorites" }]}>
            <Container styleX={styles.favoriteTabContainer}>
                <Typography textElement="h1" fontStyle="hXL">
                    {resources.title}
                </Typography>

                <Carousel
                    fullWidth={true}
                    showArrows={false}
                    itemsToShow={menuList.length}
                    styleX={styles.linksContainer}
                    carouselContainerStyleX={styles.linksList}
                >
                    {menuList.map((item) => (
                        <Button
                            key={item.text}
                            as="button"
                            variant="transparent"
                            size="M"
                            onClick={() => updateCurrentTab(item.tab || undefined)}
                            styleX={[styles.link, currentTab === item.tab && styles.activeLink]}
                        >
                            {item.text}
                        </Button>
                    ))}
                </Carousel>

                {(showAllTab || currentTab === "brands") && (
                    <FavoriteItem
                        title={resources.brand.title}
                        linkHref={`${FollowedBrands}`}
                        linkText={resources.brand.button}
                        noResultsText={
                            isLoading || data.brandsList?.length > 0 ? undefined : resources.brand.emptyText
                        }
                    >
                        <Carousel
                            fullWidth={true}
                            showArrows={!isMobileOrTablet}
                            itemsToShow={isMobileOrTablet ? 3 : 5}
                        >
                            {(data.brandsList.length > 0
                                ? data.brandsList
                                : isLoading
                                  ? Array.from<ILogoTile>({ length: isMobileOrTablet ? 3 : 5 })
                                  : []
                            ).map((i, idx) => (
                                <RoundTile
                                    key={i?.id || idx}
                                    id={i?.id}
                                    image={i?.logo}
                                    telemetryCustom={{
                                        eventName: "BrandClick",
                                        itemId: i?.id,
                                        actionType: "Click",
                                        origin: "Favorites",
                                    }}
                                    href={i?.href}
                                    showSave
                                />
                            ))}
                        </Carousel>
                    </FavoriteItem>
                )}

                {(showAllTab || currentTab === "offers") && (
                    <FavoriteItem
                        title={resources.offers.title}
                        linkHref={OffersIndex}
                        linkText={resources.offers.button}
                        noResultsText={
                            !isLoading && data.offersList.length <= 0 ? resources.offers.emptyText : undefined
                        }
                    >
                        <Carousel
                            fullWidth={true}
                            showArrows={!isMobileOrTablet}
                            itemsToShow={isMobileOrTablet ? 1.1 : 2}
                        >
                            {(data.offersList.length > 0 && !isLoading
                                ? data.offersList
                                : Array.from<IDiscountTileV3>({ length: isMobileOrTablet ? 1 : 2 })
                            ).map((i, idx) => (
                                <DiscountTileV3
                                    key={i?.id || idx}
                                    id={i?.id}
                                    brandId={i?.brandId}
                                    brandName={i?.brandName}
                                    title={i?.title}
                                    brandLogo={i?.brandLogo}
                                    discountType={i?.discountType}
                                    likesCount={i?.likesCount}
                                    navigateBrandUrl={i?.navigateBrandUrl}
                                    navigateDiscountUrl={i?.navigateDiscountUrl}
                                    discountImageLoader={i?.discountImageLoader}
                                    origin="Favorites"
                                    isLiked
                                />
                            ))}
                        </Carousel>
                    </FavoriteItem>
                )}

                {(showAllTab || currentTab === "shoppingMalls") && (
                    <FavoriteItem
                        title={resources.shoppingMall.title}
                        linkHref={ShoppingMalls}
                        linkText={resources.shoppingMall.button}
                        noResultsText={
                            !isLoading && data.shoppingMallsList.length <= 0
                                ? resources.shoppingMall.emptyText
                                : undefined
                        }
                    >
                        <Carousel
                            fullWidth={true}
                            showArrows={!isMobileOrTablet}
                            itemsToShow={isMobileOrTablet ? 1.2 : 2}
                        >
                            {(data.shoppingMallsList.length > 0 && !isLoading
                                ? data.shoppingMallsList
                                : Array.from<IShoppingMallTile>({ length: isMobileOrTablet ? 1 : 2 })
                            ).map((i, idx) => (
                                <ShoppingMallTile
                                    key={i?.id || idx}
                                    id={i?.id}
                                    title={i?.title}
                                    mallLogo={i?.logo?.src}
                                    navigateMallUrl={i?.navigateUrl}
                                    mallImageLoader={i?.image}
                                    origin="Favorites"
                                    mapLink={i?.mapLink}
                                    mapAnchor={"Location"}
                                    mapLinkText={resources.shoppingMall.mapLinkText}
                                />
                            ))}
                        </Carousel>
                    </FavoriteItem>
                )}
            </Container>
        </PageLayout>
    );
}

const styles = stylex.create({
    favoriteTabContainer: {
        gap: ComposableSpacing.s10,
        display: "flex",
        flexDirection: "column",

        visibility: "visible",
        marginBottom: 0,
        marginTop: 0,
        paddingBottom: ComposableSpacing.s8,
    },

    link: {
        color: { default: colors.fg_BrandBlue, ":hover": colors.fg_BrandMagenta },
        textWrap: "nowrap",

        transitionDuration: "300ms",
        transitionProperty: "color",
        transitionTimingFunction: "ease-out",
    },

    activeLink: {
        color: colors.fg_BrandMagenta,
        fontWeight: 500,
    },

    linksContainer: {
        gridAutoColumns: "min-content",
        borderBottomColor: colors.b_Secondary,
        borderBottomStyle: "solid",
        borderBottomWidth: 2,
        paddingBottom: 0,
        paddingLeft: 0,
        paddingRight: 0,
        paddingTop: 0,
    },

    linksList: { paddingBottom: 0, paddingLeft: 0, paddingRight: 0, paddingTop: 0 },
});
